import format from 'string-template';
import moment from 'moment';

import { UserActivityType } from '../../../../Model/UserActivity';
import { DateFormat } from '../../../../../utils/date';
import { capitalizeFirstLetter } from '../../../../../utils/string';
import { TimeDurationFormat } from '../../../../../utils/time';
import {
  MONEY_DECIMALS,
  formatAmoutInCents,
  formatCurrencyCents,
  getCurrencySymbol,
} from '../../../../../utils/formatters';

import texts from './texts';

const PAYMENT_COLLECTION_STATUS = {
  PAUSED: 'PAYMENT_PAUSED',
  RESUMED: 'PAYMENT_RESUMED',
};

const ActivityCategory = {
  ONBOARDING: 'ONBOARDING',
  LEADS: 'LEADS',
  SUBSCRIPTION: 'SUBSCRIPTION',
  PAYMENTS: 'PAYMENTS',
  WORKOUTS: 'WORKOUTS',
  CHECK_INS: 'CHECK_INS',
  HABITS: 'HABITS',
  NUTRITION: 'NUTRITION',
};

/** `cancellationSource` when Stripe's webhook initiated the cancellation rather than a person. */
const CANCELLATION_SOURCE_STRIPE = 'STRIPE';

// Activity categories and their associated types and labels.
const activityCategories = {
  [ActivityCategory.ONBOARDING]: {
    label: texts.filterOptions.ONBOARDING,
    types: [
      UserActivityType.SIGN_UP,
      UserActivityType.POST_PAYMENT_FORM_SUBMIT,
    ],
  },
  [ActivityCategory.LEADS]: {
    label: texts.filterOptions.LEADS,
    types: [
      UserActivityType.LEAD_STATUS_CHANGE,
      UserActivityType.LEAD_CALL_PLACED,
      UserActivityType.LEAD_MSG,
    ],
  },
  [ActivityCategory.SUBSCRIPTION]: {
    label: texts.filterOptions.SUBSCRIPTION,
    types: [
      UserActivityType.SUBSCRIPTION_STATUS_CHANGE,
      UserActivityType.CANCEL,
      UserActivityType.RE_ONBOARD,
      UserActivityType.SUBSCRIPTION_CHANGE,
      UserActivityType.SUBSCRIPTION_AMENDED,
    ],
  },
  [ActivityCategory.PAYMENTS]: {
    label: texts.filterOptions.PAYMENTS,
    types: [
      UserActivityType.PAYMENT_SUCCESSFUL,
      UserActivityType.PAYMENT_FAILED,
      UserActivityType.PAYMENT_REFUNDED,
    ],
  },
  [ActivityCategory.WORKOUTS]: {
    label: texts.filterOptions.WORKOUTS,
    types: [
      UserActivityType.WORKOUT_COMPLETED,
      UserActivityType.WORKOUT_ASSIGNED,
      UserActivityType.WORKOUT_PROGRAM_ASSIGNED,
      UserActivityType.EXERCISE_OVERRIDE_REMOVED,
      UserActivityType.EXERCISE_OVERRIDE_REPLACED,
    ],
  },
  /*
    Measurements sit here rather than in a category of their own: the mobile form
    requires weight, so essentially every check-in emits a MEASUREMENT_RECORDED
    beside its CHECK_IN_SUBMITTED. Two categories would have returned the same
    submissions, and splitting them hid half of a correction — the submission,
    the measurements, and any later revision now read together.
  */
  [ActivityCategory.CHECK_INS]: {
    label: texts.filterOptions.CHECK_INS,
    types: [
      UserActivityType.CHECK_IN_SUBMITTED,
      UserActivityType.MEASUREMENT_RECORDED,
    ],
  },
  [ActivityCategory.HABITS]: {
    label: texts.filterOptions.HABITS,
    types: [
      UserActivityType.HABIT_SUBMITTED,
    ],
  },
  [ActivityCategory.NUTRITION]: {
    label: texts.filterOptions.NUTRITION,
    types: [
      UserActivityType.MEAL_PLAN_ASSIGNED,
      UserActivityType.RECIPE_LIKED,
      UserActivityType.RECIPE_DISLIKED,
      UserActivityType.RECIPE_FEEDBACK_RETRACTED,
    ],
  },
};

/*
  Each category becomes a `where('type','in',[...])` clause, which Firestore caps
  at 30 values. Nothing here is close, but a category that grew past it would
  fail at query time rather than in review, so it is worth knowing the ceiling
  exists.

  A type in no category is reachable only under the default `ALL` filter. That is
  the deliberate trade for the unfiltered query carrying no type clause: a new
  event type appears without a web change, and putting it in a category is
  follow-up polish rather than a prerequisite.
*/

/**
 * Renders an optional date field in the tab's format.
 *
 * Accepts a Firestore Timestamp or anything moment reads, and returns an empty
 * string when the field is absent or unreadable — most fields on the newer event
 * payloads are optional, and a line reading "Invalid date" is worse than one
 * without a date.
 *
 * @param {object|Date|string} value - The date field, possibly undefined.
 * @param {string} [dateFormat] - A `DateFormat` pattern.
 * @returns {string}
 */
const formatActivityDate = (value, dateFormat = DateFormat.DATE_FORMAT_COMMA) => {
  if (!value) {
    return '';
  }
  const date = typeof value.toDate === 'function' ? value.toDate() : value;
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(dateFormat) : '';
};

/**
 * Calms down a meal-time name that arrives shouting.
 *
 * `mealTimes` carries `MealTime.name`, so the value is whatever the plan's meal
 * times are actually called: generated plans use `BREAKFAST` / `LUNCH`, while a
 * coach may have typed "Pre-Workout Shake". Only an entirely uppercase value is
 * normalized — anything a coach cased themselves is left exactly as typed,
 * because blanket title-casing would mangle their wording.
 *
 * @param {string} name - The meal-time name as stored.
 * @returns {string}
 */
const formatMealTimeName = (name) => {
  const text = String(name);
  if (text !== text.toUpperCase()) {
    return text;
  }
  return capitalizeFirstLetter(text);
};

/**
 * Sentence-cases a measurement name the copy has no label for.
 *
 * The check-in measurements are a fixed set today, but a new one added
 * server-side would otherwise render as the raw field name — `bodyFat` rather
 * than "Body fat". The same degradation the event-type fallback gives.
 *
 * @param {string} name - The measurement field name.
 * @returns {string}
 */
const humanizeMeasurementName = (name) => (
  capitalizeFirstLetter(String(name).replace(/([a-z0-9])([A-Z])/g, '$1 $2'))
);

/**
 * Renders a check-in's measurements as "Weight 180 lbs, Stomach 32 in".
 *
 * The unit travels with each value because it is stored per check-in, so a
 * client who switched between metric and imperial still reads correctly.
 *
 * @param {object} measurements - Measurement name to `{ value, unit }`.
 * @returns {string}
 */
const formatMeasurements = (measurements) => Object.entries(measurements || {})
  .filter(([, entry]) => entry && typeof entry.value === 'number')
  .map(([name, { value, unit }]) => {
    const label = texts.measurementLabels[name] || humanizeMeasurementName(name);
    const unitLabel = texts.unitLabels[unit];
    return unitLabel ? `${label} ${value} ${unitLabel}` : `${label} ${value}`;
  })
  .join(', ');

/**
 * Turns an event type into a readable line when nothing else renders it.
 *
 * This is the point of the whole exercise: the log's event types are declared
 * server-side, and a type with no `case` below used to leave `content` empty,
 * which `ActivityList` renders as a lone dash under a date heading. With this,
 * an event type the server adds later shows up legibly on its own and per-type
 * copy becomes polish rather than a prerequisite.
 *
 * `MEAL_PLAN_ASSIGNED` reads as "Meal plan assigned".
 *
 * @param {string} type - The event type.
 * @returns {string}
 */
const humanizeActivityType = (type) => {
  const words = String(type || '').split('_').filter(Boolean);
  if (!words.length) {
    return texts.unknownActivity;
  }
  return capitalizeFirstLetter(words.join(' '));
};

/**
 * Generates the content and subContent strings based on the activity's type and data.
 *
 * Runs inside a render-path memo, so a field read that throws blanks the whole
 * tab rather than one row. Optional fields go through `formatActivityDate` and
 * array fields are defaulted, even where the server types mark them required.
 *
 * @param {object} activity - The activity object
 * @returns {object} The content and subContent of the activity
 */
const getActivityContent = (activity) => {
  let content = '';
  let subContent = '';

  // Defaulted because every field on the newer payloads is optional, and some
  // documents carry no `data` at all.
  const data = activity.activityData || {};
  switch (activity.type) {
    case UserActivityType.WORKOUT_COMPLETED:
      content = format(texts.workoutComplete, {
        workoutName: data.workoutName,
        assignDate: formatActivityDate(data.assignedFor),
      });
      break;

    case UserActivityType.CHECK_IN_SUBMITTED:
      content = format(texts.checkInSubmit, {
        weekText: data.weekText,
        year: data.year,
      });
      break;

    case UserActivityType.PAYMENT_SUCCESSFUL:
    case UserActivityType.PAYMENT_FAILED:
    case UserActivityType.PAYMENT_REFUNDED: {
      const amount = formatAmoutInCents(data.amount, MONEY_DECIMALS);
      const currency = getCurrencySymbol(data.currency);
      if (activity.type === UserActivityType.PAYMENT_FAILED) {
        content = format(texts.failPayment, {
          currency,
          amount,
          invoice: data.invoice,
        });
        /*
          `failureMessage`, not Stripe's `failure_message`: the trigger renames it when it
          builds the event, and it spreads the field conditionally, so a failure Stripe gave
          no message for carries nothing at all. Reading the Stripe name rendered every
          failed payment as an empty "(Reason: )".
        */
        if (data.failureMessage) {
          subContent = format(texts.paymentFailReason, { reason: data.failureMessage });
        }
      } else if (activity.type === UserActivityType.PAYMENT_REFUNDED) {
        content = format(texts.refundPayment, {
          currency,
          amount,
          invoice: data.invoice,
        });
      } else {
        content = format(texts.successPayment, {
          currency,
          amount,
          invoice: data.invoice,
        });
      }
      break;
    }

    case UserActivityType.HABIT_SUBMITTED: {
      const habitText = (data.habitStat || []).map((habit) => `“${habit}”`).join(', ');
      content = format(texts.habitCheckedOff, {
        habits: habitText,
        weekText: data.weekText,
        year: data.year,
      });
      break;
    }
    case UserActivityType.LEAD_CALL_PLACED:
      content = format(texts.callPlaced, {
        userName: data.createdBy,
      });
      break;

    case UserActivityType.LEAD_MSG:
      content = format(texts.message, {
        msgType: data.isSentMsg ? texts.send : texts.received,
        message: data.message,
        time: moment(activity.createdAt).format(TimeDurationFormat.TWELVE_HOUR_FORMAT),
      });
      break;

    case UserActivityType.POST_PAYMENT_FORM_SUBMIT:
      content = format(texts.postPaymentForSubmit, {
        time: moment(activity.createdAt).format(TimeDurationFormat.TWELVE_HOUR_FORMAT),
      });
      break;

    case UserActivityType.SIGN_UP:
      content = format(texts.signUp, {
        time: moment(activity.createdAt).format(TimeDurationFormat.TWELVE_HOUR_FORMAT),
      });
      break;

    case UserActivityType.LEAD_STATUS_CHANGE:
      content = format(texts.statusChange, {
        type: texts.lead,
        status: data.leadStatus,
      });
      break;

    case UserActivityType.SUBSCRIPTION_STATUS_CHANGE: {
      const { subscriptionStatus, actionedByUserName, breakEndDate } = data;

      if (subscriptionStatus === PAYMENT_COLLECTION_STATUS.PAUSED) {
        content = format(texts.pausedDetails, {
          userName: actionedByUserName,
          pauseEnd: formatActivityDate(breakEndDate, DateFormat.MONTH_NAME_DATE_FORMAT),
        });
      } else if (subscriptionStatus === PAYMENT_COLLECTION_STATUS.RESUMED) {
        content = format(texts.unpauseDetails, {
          userName: actionedByUserName,
        });
      } else {
        content = format(texts.statusChange, {
          type: texts.subscription,
          status: subscriptionStatus,
        });
      }

      break;
    }

    // --- Workout assignments and check-in measurements ---

    case UserActivityType.WORKOUT_ASSIGNED: {
      const {
        workoutName,
        assignedFor,
        assignedBy,
        exerciseNames,
        exerciseCount,
      } = data;
      /*
        `assignedBy` equal to the client themselves is a workout they picked, not
        one the coach programmed. Different facts about a client's week, and the
        reason the field is on the event at all.
      */
      const isSelfAssigned = !!assignedBy && assignedBy === activity.userId;
      const assignDate = formatActivityDate(assignedFor);

      if (workoutName) {
        content = format(isSelfAssigned ? texts.workoutSelfAssigned : texts.workoutAssigned, {
          workoutName,
          assignDate,
        });
      } else {
        content = format(
          isSelfAssigned ? texts.workoutSelfAssignedUnnamed : texts.workoutAssignedUnnamed,
          { assignDate },
        );
      }

      const names = (exerciseNames || []).join(', ');
      if (names) {
        subContent = format(texts.workoutAssignedExercises, {
          exerciseCount: exerciseCount || (exerciseNames || []).length,
          exerciseNames: names,
        });
      }
      break;
    }

    /*
      A whole program assigned in one act (automated onboarding), in place of a
      row per program day. `assignedFor` is not shown: it is whichever day's
      assignment the server recorded first, not the program's start.
    */
    case UserActivityType.WORKOUT_PROGRAM_ASSIGNED: {
      const { programName, workoutCount } = data;
      content = programName
        ? format(texts.programAssigned, { programName })
        : texts.programAssignedUnnamed;
      if (typeof workoutCount === 'number') {
        subContent = format(texts.programAssignedWorkouts, { workoutCount });
      }
      break;
    }

    case UserActivityType.MEASUREMENT_RECORDED: {
      const {
        isCorrection,
        weekText,
        year,
        measurements,
      } = data;
      /*
        Editing a submitted check-in appends a second event rather than rewriting
        the first, so both are in the log. Separate copy keeps the pair from
        reading as the client having measured themselves twice.
      */
      const hasPeriod = !!weekText && !!year;
      if (isCorrection) {
        content = hasPeriod
          ? format(texts.measurementRevised, { weekText, year })
          : texts.measurementRevisedUndated;
      } else {
        content = hasPeriod
          ? format(texts.measurementRecorded, { weekText, year })
          : texts.measurementRecordedUndated;
      }
      subContent = formatMeasurements(measurements);
      break;
    }

    // --- Meal plans and recipe feedback ---

    case UserActivityType.MEAL_PLAN_ASSIGNED: {
      const { planName, assignedVia, mealTimes } = data;
      const via = texts.mealPlanAssignedVia[assignedVia] || texts.mealPlanAssignedViaUnknown;
      content = planName
        ? format(texts.mealPlanAssigned, { planName, assignedVia: via })
        : format(texts.mealPlanAssignedUnnamed, { assignedVia: via });

      const times = (mealTimes || []).map(formatMealTimeName).join(', ');
      if (times) {
        subContent = format(texts.mealPlanMealTimes, { mealTimes: times });
      }
      break;
    }

    /*
      `recipeName` is stored on the event by the trigger, but optional — absent on
      events written before that, and where the recipe had already been deleted.
      Each of these falls back to naming no recipe rather than showing an id.
    */
    case UserActivityType.RECIPE_LIKED:
      content = data.recipeName
        ? format(texts.recipeLikedNamed, { recipeName: data.recipeName })
        : texts.recipeLiked;
      break;

    case UserActivityType.RECIPE_DISLIKED: {
      content = data.recipeName
        ? format(texts.recipeDislikedNamed, { recipeName: data.recipeName })
        : texts.recipeDisliked;
      // The reasons a client picked in the dislike sheet; absent on a bare thumb-down.
      const reasons = (data.reason || []).join(', ');
      if (reasons) {
        subContent = format(texts.recipeDislikedReasons, { reasons });
      }
      break;
    }

    case UserActivityType.RECIPE_FEEDBACK_RETRACTED: {
      /*
        Switching a rating is a delete plus a create on two documents, so it
        surfaces as a retraction followed by the new rating. Naming which way the
        withdrawn rating went is what stops the first row reading as "changed
        their mind about something".
      */
      const retracted = texts.recipePreferenceLabels[data.retracted];
      if (!retracted) {
        content = texts.recipeFeedbackRetractedUnknown;
      } else if (data.recipeName) {
        content = format(texts.recipeFeedbackRetractedNamed, {
          retracted,
          recipeName: data.recipeName,
        });
      } else {
        content = format(texts.recipeFeedbackRetracted, { retracted });
      }
      break;
    }

    // --- Subscription lifecycle ---

    case UserActivityType.CANCEL: {
      const {
        canceledAt,
        cancelAt,
        cancellationReason,
        cancellationSource,
      } = data;
      /*
        On a Stripe-driven cancellation this lands beside a
        `SUBSCRIPTION_STATUS_CHANGE` that says only "Subscription status changed
        to canceled". This line has to carry what that one cannot — the reason,
        and whether a person or Stripe did it.

        `canceledBy` is deliberately not shown: it holds a user id, and printing
        one at a coach is worse than omitting it.
      */
      if (!canceledAt && cancelAt) {
        content = format(texts.subscriptionCancelScheduled, {
          cancelDate: formatActivityDate(cancelAt, DateFormat.MONTH_NAME_DATE_FORMAT),
        });
      } else if (cancellationSource === CANCELLATION_SOURCE_STRIPE) {
        content = format(texts.subscriptionCanceledBySource, { source: texts.stripe });
      } else {
        content = texts.subscriptionCanceled;
      }

      if (cancellationReason) {
        subContent = format(texts.subscriptionCanceledReason, { reason: cancellationReason });
      }
      break;
    }

    // `RE_ONBOARD` is the internal name for a reactivation; coaches read the latter.
    case UserActivityType.RE_ONBOARD:
      content = texts.subscriptionReactivated;
      break;

    /*
      The amendment carries its own terms, so the row states them rather than
      sending a coach to Stripe: which plan, what it costs and when it starts.
      Every field is optional — `priceInCents` is absent on an amendment written
      before the payload was typed, and `supersededContractId` is absent on all of
      them — so each part is added only when it is there.
    */
    case UserActivityType.SUBSCRIPTION_AMENDED: {
      const {
        productName, priceInCents, currency, minSubscriptionMonths, startDate,
      } = data;
      content = productName
        ? format(texts.subscriptionAmended, { planName: `“${productName}”` })
        : texts.subscriptionAmendedUnnamed;

      const price = priceInCents === undefined
        ? ''
        : formatCurrencyCents(priceInCents, currency, MONEY_DECIMALS);
      // `startDate` is a plain `YYYY-MM-DD` string here, not a Timestamp.
      const start = formatActivityDate(startDate);
      if (price && start) {
        subContent = minSubscriptionMonths
          ? format(texts.subscriptionAmendedTermsWithCommitment, {
            price, startDate: start, months: minSubscriptionMonths,
          })
          : format(texts.subscriptionAmendedTerms, { price, startDate: start });
      }
      break;
    }

    case UserActivityType.SUBSCRIPTION_CHANGE: {
      const { continueWithRecurringBilling, continueWithMonthlyCommitment } = data;
      content = texts.subscriptionChanged;
      if (continueWithRecurringBilling) {
        subContent = texts.subscriptionChangedToRecurring;
      } else if (continueWithMonthlyCommitment) {
        subContent = texts.subscriptionChangedToMonthly;
      }
      break;
    }

    // --- Coach exercise overrides ---

    /*
      The handler resolves each side's exercise name after the override commits,
      but every name is optional — an exercise deleted before it ran leaves that
      side unnamed, and older events carry no names at all. Both lines fall back
      to saying only what happened. `actionedBy` is a user id and is never shown,
      for the same reason as `canceledBy` above.
    */
    case UserActivityType.EXERCISE_OVERRIDE_REPLACED: {
      const { originalExerciseName, prevReplacement, newReplacement } = data;
      content = originalExerciseName && newReplacement?.name
        ? format(texts.exerciseOverrideReplacedNamed, {
          originalName: originalExerciseName,
          newName: newReplacement.name,
        })
        : texts.exerciseOverrideReplaced;
      if (prevReplacement?.name) {
        subContent = format(texts.exerciseOverridePreviously, { prevName: prevReplacement.name });
      }
      break;
    }

    case UserActivityType.EXERCISE_OVERRIDE_REMOVED: {
      const { originalExerciseName, prevReplacement } = data;
      content = originalExerciseName
        ? format(texts.exerciseOverrideRemovedNamed, { originalName: originalExerciseName })
        : texts.exerciseOverrideRemoved;
      if (prevReplacement?.name) {
        subContent = format(texts.exerciseOverridePreviously, { prevName: prevReplacement.name });
      }
      break;
    }

    /*
      Any event type with no case above, including one this build has never seen.
      Never leave `content` empty here — `ActivityList` renders it as `- ${content}`,
      so an empty string is the bare dash this fallback exists to prevent.
    */
    default:
      content = humanizeActivityType(activity.type);
      break;
  }
  return {
    content,
    subContent,
  };
};

export {
  getActivityContent,
  activityCategories,
  humanizeActivityType,
};
